// hooks/useWebSocket.js
import { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

const useWebSocket = () => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const currentUser = useSelector((state) => state.auth.user);

  const url = `wss://pulse.tutify.online/ws/${currentUser?.uuid}`;

  const connect = useCallback(() => {
    if (!currentUser || !currentUser.uuid)
      return;

    try {
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        // console.log("WebSocket connected to:", url);
        sendTeacherStatus("online");
        startHeartbeat();
      };

      // wsRef.current.onmessage = (event) => {
      //   const message = event.data;
      //   try {
      //     const isProbablyJson =
      //       message.trim().startsWith("{") || message.trim().startsWith("[");
      //     if (isProbablyJson) {
      //       const data = JSON.parse(message);
      //       console.log("Received JSON message:", data);
      //     } else {
      //       console.log("Received plain message:", message);
      //     }
      //   } catch (error) {
      //     console.error("Error parsing message:", error);
      //   }
      // };

      wsRef.current.onclose = (event) => {
        // console.log("WebSocket disconnected:", event.code, event.reason);
        stopHeartbeat();

        if (currentUser && currentUser.id) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 30000); // Try to reconnect after 30s
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
    }
  }, [url, currentUser]);

  const sendTeacherStatus = useCallback(
    (status) => {
      if (
        wsRef.current &&
        wsRef.current.readyState === WebSocket.OPEN &&
        currentUser
      ) {
        const message = {
          status: status, // 'online', 'away', or 'offline'
        };
        wsRef.current.send(JSON.stringify(message));
        // console.log("Sent teacher status:", message);
      }
    },
    [currentUser]
  );

  const startHeartbeat = useCallback(() => {
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000); // every 30s
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    sendTeacherStatus("offline");

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    stopHeartbeat();

    if (wsRef.current) {
      wsRef.current.close(1000, "Component unmounting");
      wsRef.current = null;
    }
  }, [sendTeacherStatus, stopHeartbeat]);

  // Handle tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentUser) {
        const blob = new Blob([JSON.stringify({ status: "offline" })], {
          type: "application/json",
        });
        navigator.sendBeacon(
          `https://pulse.tutify.online/api/status/${currentUser.uuid}`,
          blob
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentUser]);

  // Handle tab visibility and focus/blur
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (currentUser) {
        if (document.visibilityState === "hidden") {
          sendTeacherStatus("away");
        } else {
          sendTeacherStatus("online");
        }
      }
    };

    const handleBlur = () => {
      if (currentUser) {
        sendTeacherStatus("away");
      }
    };

    const handleFocus = () => {
      if (currentUser) {
        sendTeacherStatus("online");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [sendTeacherStatus, currentUser]);

  useEffect(() => {
    if (currentUser && currentUser.uuid) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [currentUser, connect, disconnect]);

  return {
    sendTeacherStatus,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };
};

export default useWebSocket;
